import { redirect } from '@tanstack/react-router';
import type { AdminRole } from '@nokowazzi/types';
import { ApiError } from '../../lib/api';
import { queryClient } from '../../lib/query-client';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../stores/auth';
import { adminMeQuery } from '../admin/queries';

async function waitForAuthInit(): Promise<void> {
  if (!useAuthStore.getState().initializing) return;
  await new Promise<void>((resolve) => {
    const unsub = useAuthStore.subscribe((state) => {
      if (!state.initializing) {
        unsub();
        resolve();
      }
    });
  });
}

/**
 * 어드민 보호 라우트 가드 — _admin 레이아웃 beforeLoad에서 호출.
 * 세션 확인 → GET /v1/admin/me로 어드민 여부 확인.
 * 401/403이면 로그아웃 후 '권한 없음' 사유와 함께 로그인 화면으로 보낸다.
 */
export async function requireAdminBeforeLoad(redirectTo: string): Promise<{ role: AdminRole }> {
  await waitForAuthInit();

  if (!useAuthStore.getState().session) {
    throw redirect({ to: '/signin', search: { redirect: redirectTo, reason: undefined } });
  }

  try {
    const me = await queryClient.ensureQueryData(adminMeQuery());
    return { role: me.role };
  } catch (error) {
    if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
      await supabase.auth.signOut();
      queryClient.clear();
      throw redirect({ to: '/signin', search: { redirect: redirectTo, reason: 'forbidden' } });
    }
    throw error;
  }
}
